import React, { useState, useRef } from 'react';
import { Upload, Camera, X, Check, ArrowLeft } from 'lucide-react';
import { imagesService } from '../services/imagesService';

const ImageUploader = ({ onImageUpload, currentImage = null, onCancel = null, type = 'product' }) => {
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(
    currentImage ? imagesService.getImageUrl(currentImage) : null
  );
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFiles = async (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      setError(null);
      
      try {
        // Validar archivo
        imagesService.validateImageFile(file);

        setUploading(true);

        // Crear preview local
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreview(e.target.result);
        };
        reader.readAsDataURL(file);

        // Subir archivo al servidor usando el método correcto según el tipo
        const response = type === 'truck' 
          ? await imagesService.uploadTruckImage(file)
          : type === 'requisition'
          ? await imagesService.uploadRequisitionImage(file) // Usar el método específico para requisiciones
          : await imagesService.uploadImage(file);
        
        if (response.success) {
          const imageUrl = imagesService.getImageUrl(response.data.filename);
          setPreview(imageUrl);
          onImageUpload(response.data.filename);
          console.log('✅ Imagen subida exitosamente:', imageUrl);
        } else {
          throw new Error(response.message || 'Error subiendo imagen');
        }

      } catch (error) {
        console.error('❌ Error procesando imagen:', error);
        setError(error.message);
        setPreview(null);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer?.files;
    handleFiles(files);
  };

  const handleInputChange = (e) => {
    const files = e.target.files;
    handleFiles(files);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const removeImage = async () => {
    try {
      if (preview && preview.includes('/images/products/')) {
        // Extraer nombre del archivo de la URL
        const filename = imagesService.getFilenameFromUrl(preview);
        if (filename) {
          await imagesService.deleteImage(filename);
          console.log('✅ Imagen eliminada del servidor');
        }
      }
    } catch (error) {
      console.error('❌ Error eliminando imagen del servidor:', error);
      // Continuar con la eliminación local aunque falle la del servidor
    }
    
    setPreview(null);
    setError(null);
    onImageUpload(null);
  };

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
      />
      
      {/* Mostrar errores */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg">
          <p className="text-sm">{error}</p>
        </div>
      )}
      
      {preview ? (
        // Vista previa de imagen
        <div className="relative bg-gray-50 flex items-center justify-center">
          <img
            src={preview}
            alt="Preview"
            className="max-w-full max-h-48 object-contain rounded-lg border border-gray-300"
            onError={(e) => {
              console.error('Error cargando imagen:', e);
              setError('Error cargando la imagen');
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyLjIwMDcgMTAuNjY2N0w4Ljg2NzM3IDcuMzMzMzRMMTIuMjAwNyA0TTMuNzM0MDQgNUgxMC4wNjc0TTMuNzM0MDQgMTBIMTAuMDY3NCIgc3Ryb2tlPSIjOTk5OTk5IiBzdHJva2Utd2lkdGg9IjEuMzMzMzMiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4=';
              e.target.classList.add('p-8', 'bg-gray-100');
            }}
          />
          
          {uploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
              <div className="text-white text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                <p className="text-sm">Subiendo imagen...</p>
              </div>
            </div>
          )}
          
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              onClick={openFileDialog}
              disabled={uploading}
              className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title="Cambiar imagen"
            >
              <Camera className="w-4 h-4" />
            </button>
            
            <button
              onClick={removeImage}
              disabled={uploading}
              className="bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title="Eliminar imagen"
            >
              <X className="w-4 h-4" />
            </button>
            
            {onCancel && (
              <button
                onClick={onCancel}
                disabled={uploading}
                className="bg-gray-600 text-white p-2 rounded-full hover:bg-gray-700 transition-colors duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                title="Cancelar y cerrar"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      ) : (
        // Zona de arrastrar y soltar
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-300 cursor-pointer
            ${dragActive 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
            }
            ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={!uploading ? openFileDialog : undefined}
        >
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Upload className={`w-8 h-8 ${dragActive ? 'text-blue-600' : 'text-blue-500'}`} />
            </div>
            
            <div>
              <p className="text-lg font-medium text-gray-700">
                {uploading ? 'Subiendo imagen...' : 
                 dragActive ? 'Suelta la imagen aquí' : 
                 type === 'requisition' ? 'Subir imagen de requisición' :
                 type === 'truck' ? 'Subir imagen de unidad' :
                 'Subir imagen del producto'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {uploading ? 'Por favor espera...' :
                 'Arrastra y suelta una imagen o haz clic para seleccionar'}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Formatos soportados: JPG, PNG, GIF, WEBP (máx. 5MB)
              </p>
            </div>
            
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                disabled={uploading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Camera className="w-4 h-4" />
                {uploading ? 'Subiendo...' : 'Seleccionar Archivo'}
              </button>
              
              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Cancelar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;